import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useShop } from '../../../shared/store';
import { orderService } from '../../../shared/services/orderService';
import type { CheckoutPayload, PaymentMethod, ShippingAddress } from '../../../shared/types';

interface CheckoutPaymentRouteState {
  shippingAddress?: ShippingAddress;
  paymentMethod?: PaymentMethod;
  podProjectId?: string;
  podOrderId?: string;
}

interface PendingCheckoutPaymentSession {
  paymentMethod: PaymentMethod;
  shippingAddress: ShippingAddress;
  podProjectId?: string;
  podOrderId?: string;
  orderId?: string;
  paymentReference?: string;
  paymentUrl?: string;
  subtotal?: number;
  shippingFee?: number;
  total?: number;
}

const PENDING_PAYMENT_STORAGE_KEY = 'haapa.checkout.pendingPayment';

const readPendingPayment = (): PendingCheckoutPaymentSession | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.sessionStorage.getItem(PENDING_PAYMENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PendingCheckoutPaymentSession;
  } catch {
    return null;
  }
};

const persistPendingPayment = (session: PendingCheckoutPaymentSession | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!session) {
    window.sessionStorage.removeItem(PENDING_PAYMENT_STORAGE_KEY);
    return;
  }

  window.sessionStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, JSON.stringify(session));
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const resolveFlutterwaveUrl = (session: PendingCheckoutPaymentSession): string | undefined => {
  if (session.paymentUrl) {
    return session.paymentUrl;
  }

  if (!session.paymentReference) {
    return undefined;
  }

  // Hosted checkout links typically use a `flwlnk-` token.
  if (session.paymentReference.startsWith('flwlnk-')) {
    return `https://checkout.flutterwave.com/v3/hosted/pay/${session.paymentReference}`;
  }

  return undefined;
};

const buildFlutterwaveCheckoutUrl = (
  paymentUrl: string,
  callbackUrl: string,
  reference?: string,
): string => {
  try {
    const url = new URL(paymentUrl);
    if (!url.searchParams.get('redirect_url')) {
      url.searchParams.set('redirect_url', callbackUrl);
    }
    if (reference && !url.searchParams.get('tx_ref')) {
      url.searchParams.set('tx_ref', reference);
    }
    return url.toString();
  } catch {
    return paymentUrl;
  }
};

const CheckoutPaymentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartSubtotal, cart } = useShop();
  const routeState = (location.state as CheckoutPaymentRouteState | null) ?? null;

  const [pendingSession, setPendingSession] = useState<PendingCheckoutPaymentSession | null>(null);
  const [formError, setFormError] = useState('');
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  useEffect(() => {
    const session = readPendingPayment();
    if (routeState?.shippingAddress && routeState.paymentMethod) {
      const nextSession: PendingCheckoutPaymentSession = {
        ...(session ?? {}),
        shippingAddress: routeState.shippingAddress,
        paymentMethod: routeState.paymentMethod,
        podProjectId: routeState.podProjectId ?? session?.podProjectId,
        podOrderId: routeState.podOrderId ?? session?.podOrderId,
      };

      setPendingSession(nextSession);
      persistPendingPayment(nextSession);
      return;
    }

    if (session) {
      setPendingSession(session);
      return;
    }

    void navigate('/checkout', { replace: true });
  }, [navigate, routeState]);

  useEffect(() => {
    if (!pendingSession) {
      return;
    }

    if (pendingSession.paymentMethod !== 'card') {
      void navigate('/checkout', { replace: true });
    }
  }, [navigate, pendingSession]);

  const shippingFee = useMemo(() => 1500, []);
  const total = cartSubtotal + shippingFee;
  const summarySubtotal = pendingSession?.subtotal ?? cartSubtotal;
  const summaryShippingFee = pendingSession?.shippingFee ?? shippingFee;
  const summaryTotal = pendingSession?.total ?? total;

  const createOrderIfNeeded = async (): Promise<PendingCheckoutPaymentSession> => {
    if (!pendingSession) {
      throw new Error('Missing checkout details. Please return to checkout.');
    }

    if (pendingSession.orderId && (pendingSession.paymentReference || pendingSession.paymentUrl)) {
      return pendingSession;
    }

    const callbackUrl = `${window.location.origin}/checkout/payment/callback`;
    const payload: CheckoutPayload = {
      paymentMethod: pendingSession.paymentMethod,
      shippingAddress: pendingSession.shippingAddress,
      podProjectId: pendingSession.podProjectId,
      podOrderId: pendingSession.podOrderId,
      redirectUrl: callbackUrl,
      callbackUrl,
    };

    setIsCreatingOrder(true);
    try {
      const result = await orderService.checkout(payload);
      const record = asRecord(result);
      const dataRecord = asRecord(record.data);

      const resolved =
        (dataRecord.data && typeof dataRecord.data === 'object' ? (dataRecord.data as Record<string, unknown>) : null) ||
        dataRecord ||
        record;

      const resolvedRecord = asRecord(resolved);
      const resolvedPayment = asRecord(resolvedRecord.payment);

      const nextSession: PendingCheckoutPaymentSession = {
        ...pendingSession,
        orderId:
          asString(resolvedRecord.id) ||
          asString(resolvedRecord.orderId) ||
          asString(asRecord(resolvedRecord.order).id) ||
          asString(asRecord(resolvedRecord.order)._id) ||
          '',
        paymentReference:
          asString(resolvedRecord.paymentReference) ||
          asString(resolvedRecord.tx_ref) ||
          asString(resolvedRecord.reference) ||
          asString(resolvedPayment.reference) ||
          asString(resolvedPayment.tx_ref),
        paymentUrl:
          asString(resolvedRecord.paymentUrl) ||
          asString(resolvedRecord.paymentLink) ||
          asString(resolvedRecord.link) ||
          asString(resolvedPayment.paymentUrl) ||
          asString(resolvedPayment.paymentLink) ||
          asString(resolvedPayment.link),
        subtotal: cartSubtotal,
        shippingFee,
        total,
      };

      if (!nextSession.paymentReference && !nextSession.paymentUrl) {
        throw new Error('Payment link is not ready yet. Please try again.');
      }

      setPendingSession(nextSession);
      persistPendingPayment(nextSession);
      return nextSession;
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleOpenFlutterwave = async () => {
    setFormError('');
    try {
      const session = await createOrderIfNeeded();
      const paymentUrl = resolveFlutterwaveUrl(session);
      if (!paymentUrl) {
        throw new Error('Unable to open payment page. Backend must return Flutterwave hosted `link`/`paymentUrl`.');
      }

      const callbackUrl = `${window.location.origin}/checkout/payment/callback`;
      const nextUrl = buildFlutterwaveCheckoutUrl(paymentUrl, callbackUrl, session.paymentReference);
      window.location.assign(nextUrl);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFormError(err.message || 'Unable to continue to payment.');
    }
  };

  if (!pendingSession) {
    return (
      <section className="animate-fade-up rounded-3xl border border-orange-100 bg-white p-8 text-center shadow-lg">
        <p className="text-sm text-slate-600">{t('common.loading')}</p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <header className="animate-fade-up">
        <h2 className="text-3xl font-semibold text-orange-700">Flutterwave Payment</h2>
        <p className="mt-2 text-sm text-slate-600">
          Complete payment on Flutterwave. You will be returned automatically to finalize your order.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
        <article className="animate-fade-up space-y-5 rounded-3xl border border-orange-100 bg-white p-5 shadow-lg">
          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-orange-700">Delivery Address</h3>
            <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">{pendingSession.shippingAddress.fullName}</p>
              <p>{pendingSession.shippingAddress.phone}</p>
              <p>{pendingSession.shippingAddress.street}</p>
              <p>
                {pendingSession.shippingAddress.city}, {pendingSession.shippingAddress.country}
              </p>
              <p>{pendingSession.shippingAddress.postalCode}</p>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="text-lg font-semibold text-orange-700">How It Works</h3>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-700">
              <li>Click "Continue to Flutterwave".</li>
              <li>Complete your payment in Flutterwave.</li>
              <li>You will be returned automatically to complete your order.</li>
            </ol>
          </section>

          {formError && (
            <p role="alert" className="text-sm font-semibold text-red-600">
              {formError}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleOpenFlutterwave}
              disabled={isCreatingOrder}
              className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
                isCreatingOrder
                  ? 'cursor-not-allowed bg-orange-300'
                  : 'bg-orange-500 hover:bg-orange-600'
              }`}
            >
              {isCreatingOrder ? 'Preparing Payment...' : 'Continue to Flutterwave'}
            </button>

            <Link
              to="/checkout"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Back to Checkout
            </Link>
          </div>
        </article>

        <aside className="animate-fade-up space-y-4 rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-orange-700">{t('checkout.orderSummary')}</h3>
          <dl className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-slate-500">{t('cart.itemsWithCount', { count: cart.length })}</dt>
              <dd className="font-semibold text-orange-700">{cart.length}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-500">{t('cart.subtotal')}</dt>
              <dd className="font-semibold text-orange-700">$ {summarySubtotal.toFixed(2)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-slate-500">{t('cart.shipping')}</dt>
              <dd className="font-semibold text-orange-700">
                {summaryShippingFee === 0 ? t('cart.free') : `$ ${summaryShippingFee.toFixed(2)}`}
              </dd>
            </div>
            <div className="flex items-center justify-between border-t border-orange-100 pt-2">
              <dt className="text-slate-700">{t('cart.total')}</dt>
              <dd className="text-base font-bold text-orange-700">$ {summaryTotal.toFixed(2)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </section>
  );
};

export default CheckoutPaymentPage;
